// Arizona State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - ASU's Common Data Set 2025-2026 for ASU Campus Immersion (the in-person
//    campuses; ASU Online files its own), the PDF "CDS 2025-26 - ASU Campus
//    Immersion.pdf" that the University Office of Institutional Analysis
//    links from its Common Data Set page: C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21/C22 plans, G1/G5 2026-2027 costs (not marked
//    provisional).
//  - ASU Admission, First-year student: "Here are important deadlines you
//    need to know for fall 2027" (the Nov. 1 priority and Jan. 15, 2027
//    regular admission dates), the aptitude and competency requirements, the
//    testing step and FAQs, Barrett and the higher-requirement majors. The
//    page says decisions "begin the first week of September" and gives no
//    decision date per deadline.
//  - College Scorecard, school id 104151 ("Arizona State University Campus
//    Immersion", net price by family income), read from the Scorecard API on
//    2026-10-01.
// Cost: the CDS G1/G5 figures. Out-of-state tuition is the CDS's
// "Out-of-state" line ($36,065); its separate "Non-resident" line ($39,412),
// the CDS's line for international students, is not shown. ASU's tuition
// varies by program (CDS G4), so these are the typical figures the CDS
// reports.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'ASU Common Data Set 2025-2026, Campus Immersion',
        url: 'https://uoia.asu.edu/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'ASU Admission, First-year student: admission requirements and deadlines',
        url: 'https://admission.asu.edu/first-year/apply',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?104151-Arizona-State-University-Campus-Immersion',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'arizona-state-university',
    kbId: 'arizona_state_university',
    name: 'Arizona State University',
    shortName: 'Arizona State',
    aliases: ['ASU'],
    location: 'Tempe, AZ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (ASU brand guide color palette); decoration only.
    branding: {
        primary: '#8C1D40',
        secondary: '#FFC627',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/arizona_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 69617,
        admitted: 61533,
        enrolled: 13665,
        source: 'cds',
        where: 'section C1 (ASU Campus Immersion)',
        residency: [
            { group: 'In-state', applied: 20323, admitted: 18291 },
            { group: 'Out-of-state', applied: 40388, admitted: 35233 },
            { group: 'International', applied: 8906, admitted: 8009 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'ASU\'s Common Data Set reports one first-year acceptance rate for its in-person campuses, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'ASU has no early decision or early action plan. Its November 1 priority admission date is an earlier filing date, not an early plan. Barrett, The Honors College has its own November 1 early action deadline for the honors application only.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'One application covers ASU. Two things work differently:',
        rows: [
            { school: 'Majors with higher requirements', requirement: 'Some degree programs have higher aptitude requirements than the university\'s general ones; ASU\'s Degree Search lists them by program. If your first-choice major has higher requirements, you will be asked to choose a second major without them.' },
            { school: 'Barrett, The Honors College', requirement: 'A separate application. It opens in early September and its early action deadline is November 1. ASU highly recommends applying to the university before that deadline, since the honors application needs your ASU credentials.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission date', value: 'January 15' },
        rows: [
            { plan: 'Priority admission date', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Regular admission date', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'The fall 2027 application opens in early July, on ASU\'s own application or the Common Application (one only). Applying by November 1 makes sure you are considered for the most scholarship opportunities. ASU says admission decisions begin the first week of September and does not publish a decision date for either date. FAFSA priority filing date: January 15. The application fee is $55 for Arizona residents and $85 for domestic nonresidents.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'ACT or SAT scores are not required for admission at ASU, but may be sent for course placement or as supplemental information. Besides the competency courses, you need one of: the top 25% of your graduating class, a 3.00 unweighted GPA in competency courses, an ACT of 22 (24 for nonresidents) or an SAT of 1120 (1180 for nonresidents). ASU highly recommends sending a score for merit scholarship consideration.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by August 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [12527, 36065] },
            { item: 'Required fees', amounts: [1007, 1007] },
            { item: 'Housing (on campus)', amounts: [11473, 11473] },
            { item: 'Food (on-campus meal plan)', amounts: [7346, 7346] },
            { item: 'Books and supplies', amounts: [1320, 1320] },
            { item: 'Transportation', amounts: [1650, 1650] },
            { item: 'Other expenses', amounts: [2415, 2415] },
        ],
        totals: [37738, 61276],
        note: 'For a full-time undergraduate living on campus. Tuition at ASU varies by program; these are the typical figures the Common Data Set reports.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 14694,
            byIncome: [
                { income: '$0 to $30,000', amount: 9413 },
                { income: '$30,001 to $48,000', amount: 10689 },
                { income: '$48,001 to $75,000', amount: 15698 },
                { income: '$75,001 to $110,000', amount: 18479 },
                { income: 'Over $110,000', amount: 24047 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from ASU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
